// "Scheduled" is DERIVED, never a status — there is deliberately no SCHEDULED enum value on the wire.
// A task the agent will only pick up later is an ordinary PENDING task carrying a future `notBefore`, so
// a surface that never implements this still renders the row correctly as pending work. Shared 1:1 with
// iOS/Android; keep the rule here and nowhere else.
//
// Import-free on purpose (see orion-web/CLAUDE.md → Tests): the bucket map, the card and the list all read
// it, and a jest test must be able to exercise it without dragging the app's dependency graph in.

/** The minimum a row needs for the scheduled rule. Satisfied by `AgentTask` and by web's `WebTask`. */
export interface SchedulableTask {
  status?: string;
  /** ISO timestamp the agent may not start before. Absent on everything that runs now. */
  notBefore?: string | Date;
  /**
   * When a marketing-plan post goes OUT, ISO (`display.marketingPlanPost.postAt`). A post step's `notBefore`
   * is when the agent starts preparing it, the day BEFORE the post, so a date shown to the owner reads this
   * first ({@link plannedForMs}). Absent on everything that is not a plan post.
   */
  postAt?: string | Date;
  /**
   * The worker agent's own conversation — the durable record that an agent has BEEN PUT ON this task.
   * Read here only by {@link isNotStartedTask}; see that function for why it is the right signal.
   */
  agentConversationId?: string;
  /**
   * The tasks this one waits for — the work edge (`AgentTask.blocking_agent_task_ids`) joined to the
   * blockers' title/status/date by the route, because a client cannot render "After Check-in round 4" from
   * an id. Present only when the task has blockers. Read by {@link isGatedTask} and friends.
   */
  blockedByTasks?: TaskBlockerRef[];
  /**
   * The open questions this task is held on. Only the id is needed HERE — the three derived states below
   * read nothing but "is there one" — so the shape is the structural minimum, which keeps this module
   * import-free exactly as its doc above requires. `BucketableTask` widens it to the full prompt.
   *
   * **Absent or non-empty, NEVER `[]`.**
   */
  decisionPrompts?: { decisionId: string }[];
  /**
   * Is an agent loop executing on this task RIGHT NOW, when the upstream computed it? **Tri-state: absent
   * means "not computed", never `false`.** Read here only by {@link isNotStartedTask}; `BucketableTask`
   * widens the pair (`runInFlight` / `isLive`) that `isInMotion` reads.
   */
  runInFlight?: boolean;
}

/**
 * Whether this row is holding an OPEN question at the reader — the fact that outranks all three derived
 * states below.
 *
 * It lives here, in the import-free module, because `isScheduledTask` / `isGatedTask` / `isNotStartedTask`
 * each have to ask it and they are the lowest layer. `awaitsDecision` in `agent-task-buckets` delegates
 * to it so there is exactly one definition.
 */
export function hasOpenDecision(task: SchedulableTask): boolean {
  return (task.decisionPrompts?.length ?? 0) > 0;
}

/** One blocker of a gated task, as the routes join it. `notBefore` only when that blocker carries one. */
export interface TaskBlockerRef {
  id: string;
  title: string;
  status: string;
  notBefore?: string | Date;
}

/** A blocker that no longer blocks: it finished, or it was called off / retired (FAILED still blocks — a
 *  dependent still needs that work, see the AgentTask Status doc). Shared 1:1 with iOS/Android. */
export function isSettledTaskStatus(status: string | undefined): boolean {
  return status === 'COMPLETED' || status === 'CANCELLED' || status === 'MOOT';
}

export function unsettledBlockers(task: SchedulableTask): TaskBlockerRef[] {
  return (task.blockedByTasks ?? []).filter((blocker) => !isSettledTaskStatus(blocker.status));
}

/**
 * gated := status == PENDING && !scheduled && at least one blocker is unsettled.
 *
 * The THIRD derived state beside `scheduled` and `notStarted`, and derived for the same reason: there is no
 * GATED on the wire, and a surface that never implements this still renders the row as the pending work it
 * is. It answers the question the "Not started" chip could not — Ronny's "is this scheduled?" on a digest
 * task that waits for its round's check-in — with WHAT it waits for. Scheduled wins when both apply: a
 * dated task's own chip already says why nothing is moving, and says WHEN, which is the more useful half.
 * Callers apply "awaiting decision" ahead of this, exactly as they do for `notStarted`.
 *
 * **Unlike its siblings, this one never prefers the server's `displayStatus` — ON PURPOSE.** `bucket`,
 * `inMotion`, `scheduled` and `notStarted` all take the wire verdict when it is present; gated does not,
 * because web's chip does not read "Blocked", it reads "After {blocker}", and the name comes from
 * `blockedByTasks` via `gatingBlocker`. Taking the boolean from one source and the name from another
 * admits a state that is gated with no name to render. The wire keeps publishing `gated` for the phones,
 * whose badge needs no name; here the shared fixture (`test/task-status-contract.test.ts`) is what keeps
 * this derivation and the server's answer in agreement.
 */
export function isGatedTask(task: SchedulableTask, now: number = Date.now()): boolean {
  if (task.status !== 'PENDING') return false;
  // A question outranks an edge, and the precedence belongs IN the predicate rather than at each call
  // site. Web used to leave it to the caller and both phones did not, which is how three surfaces came to
  // hold three different versions of one rule while every surface's own tests stayed green.
  if (hasOpenDecision(task)) return false;
  if (isScheduledTask(task, now)) return false;
  return unsettledBlockers(task).length > 0;
}

/** The blocker the chip names: the first unsettled one. `undefined` when the task is not gated. */
export function gatingBlocker(task: SchedulableTask, now: number = Date.now()): TaskBlockerRef | undefined {
  return isGatedTask(task, now) ? unsettledBlockers(task)[0] : undefined;
}

/**
 * When this task can start at the earliest, as far as the wire can tell: its own `notBefore`, else the
 * LATEST `notBefore` among its unsettled blockers (it cannot start before the last of them may). `null`
 * when nothing dates it. ONE hop by design — a blocker's own blockers are not on the wire — so a task gated
 * on an undated task that is itself gated on a dated one reads as undated here. Feeds the goal Timeline.
 */
export function effectiveStartMs(task: SchedulableTask, now: number = Date.now()): number | null {
  const own = notBeforeMs(task);
  if (own !== null) return own;
  if (task.status !== 'PENDING') return null;
  let latest: number | null = null;
  for (const blocker of unsettledBlockers(task)) {
    const at = notBeforeMs({ notBefore: blocker.notBefore });
    if (at !== null && (latest === null || at > latest)) latest = at;
  }
  void now;
  return latest;
}

/**
 * The day the OWNER expects this task, as ms: a plan post's `postAt` (when it goes out), else
 * {@link effectiveStartMs}. A post step starts the day before its post, so dating it by `notBefore` showed
 * every post a day early (Yakir's plan review, 2026-11-01). Shared 1:1 with iOS `plannedFor` and Android
 * `taskPlannedFor`.
 */
export function plannedForMs(task: SchedulableTask, now: number = Date.now()): number | null {
  if (task.postAt) {
    const ms = new Date(task.postAt).getTime();
    if (!Number.isNaN(ms)) return ms;
  }
  return effectiveStartMs(task, now);
}

/** `display.marketingPlanPost.postAt` (written by the harness's LayOutMarketingPlan), ISO, or undefined. */
export function marketingPlanPostAt(display: unknown): string | undefined {
  const post = (display as { marketingPlanPost?: { postAt?: unknown } } | undefined)?.marketingPlanPost;
  const at = typeof post?.postAt === 'string' ? new Date(post.postAt) : undefined;
  return at && !Number.isNaN(at.getTime()) ? at.toISOString() : undefined;
}

export function deliveryPublishAt(display: unknown): string | undefined {
  const raw = (display as { delivery?: { publishAt?: unknown } } | undefined)?.delivery?.publishAt;
  const at = typeof raw === 'string' ? new Date(raw) : undefined;
  return at && !Number.isNaN(at.getTime()) ? at.toISOString() : undefined;
}

export function withGoOutAt<T extends { postAt?: string; deliveryAt?: string }>(row: T, publishTime: boolean): T {
  return publishTime && row.deliveryAt ? { ...row, postAt: row.deliveryAt } : row;
}

/** `notBefore` as a sortable number; `null` when absent or unparseable — never a throw, never a 0. */
export function notBeforeMs(task: SchedulableTask): number | null {
  if (!task.notBefore) return null;
  const ms = new Date(task.notBefore).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/**
 * scheduled := status == PENDING && notBefore != null && notBefore > now.
 *
 * Deliberately narrow: a PENDING row whose `notBefore` has passed is queued work the agent may take at any
 * moment, not something waiting for a date, so it reads as an ordinary live row.
 */
export function isScheduledTask(task: SchedulableTask, now: number = Date.now()): boolean {
  if (task.status !== 'PENDING') return false;
  // A task WAITING ON THE READER is not "later" — it is now, and it is theirs. A row that is both dated
  // and blocked satisfies both, and the quiet "Later" treatment files the one row they have to act on
  // under a heading that says it can be ignored until then.
  if (hasOpenDecision(task)) return false;
  const at = notBeforeMs(task);
  return at !== null && at > now;
}

/**
 * notStarted := status == PENDING && !scheduled && nobody has ever been put on it.
 *
 * The SIBLING of `isScheduledTask`, and derived for the same reason: there is no NOT_STARTED on the wire,
 * so a surface that never implements this still renders the row as the pending work it is.
 *
 * A task the agent deliberately laid out and has not begun buckets as In progress (PENDING is the agent's
 * turn, not the user's — see `bucketContaining`), which is correct for counting and wrong for reading: two
 * goal-laid-out tasks sat on a real Tasks screen saying "In progress" with no agent anywhere near them.
 *
 * **The "never started" half is a POSITIVE signal, not an absence.** `agentConversationId` is written when
 * a task's agent is dispatched (`dispatch-task-agent.ts`) and is deliberately KEPT after that loop dies
 * (`ensure-task-agent-running.ts` says so in as many words), so its presence is a durable "an agent has
 * been on this" record rather than a guess about current activity. Nothing else on the row can answer the
 * question: the changeLog label vocabulary (`AGENT_TASK_CHANGE_LABEL`) has no `started` entry, and
 * `runInFlight` answers "is a loop running NOW", which is false for a task that ran yesterday and stopped.
 * `createGoalTask` creates exactly this shape — PENDING, no agent conversation, "laid out but idle".
 *
 * Scheduled wins when both apply: a dated task's own quiet treatment already says why nothing is moving,
 * and it says something more useful (WHEN) than "not started" would.
 */
export function isNotStartedTask(task: SchedulableTask, now: number = Date.now()): boolean {
  if (task.status !== 'PENDING') return false;
  // Its own guard, not inherited from the two below: both of those now refuse a blocked row, so without
  // this a PENDING task holding an open question and no agent would fall all the way through and wear the
  // quiet "Not started" chip — muting the one row that is asking for something. It is not started BECAUSE
  // it waits on an answer, which is what the Action-needed badge says instead.
  if (hasOpenDecision(task)) return false;
  // A live loop RIGHT NOW is by definition a start, whatever the durable dispatch record says. Android
  // had this guard alone; it is the shared rule now, because WITHOUT it a row can be both in motion and
  // "not started" at once — `isInMotion` returns the verdict while the badge reads "Not started", a
  // spinner turning under a chip that says nothing has begun. `false`/absent fall through: "no live run
  // this minute" is not "never dispatched".
  if (task.runInFlight === true) return false;
  if (isScheduledTask(task, now)) return false;
  // Gated wins over not-started for the same reason scheduled does: "After Check-in round 4" says why
  // nothing is moving; "Not started" only says that it isn't.
  if (isGatedTask(task, now)) return false;
  return !task.agentConversationId;
}

const WEEK_MS = 7 * 86400 * 1000;

/**
 * The scheduled row's chip: the short weekday ("Thu") while the date is inside the coming week, a short
 * date ("14 Mar") beyond it. The locale's own `toLocaleDateString` decides the day/month order, so an
 * `en-GB` reader gets "14 Mar" and an `en-US` one "Mar 14" — same rule the activity feed already follows.
 * Empty string on an absent/unparseable date, so a caller can render nothing rather than "Invalid Date".
 */
export function scheduledDateLabel(
  value: string | Date | undefined,
  now: number = Date.now(),
  locale?: string,
): string {
  const at = notBeforeMs({ notBefore: value });
  if (at === null) return '';
  const date = new Date(at);
  if (at - now < WEEK_MS) return date.toLocaleDateString(locale, { weekday: 'short' });
  return date.toLocaleDateString(locale, { day: 'numeric', month: 'short' });
}
